import React, { useRef, useEffect } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AgentChatMessageBubble from "../common/AgentChatMessageBubble";
import ContextWindowMeter from "../common/ContextWindowMeter";
import type { ProductionSupportChatMessage } from "../../hooks/useProductionSupportAgentChat";
import type { AgentContextUsage } from "../../types/agentChat";
import { HUB_CHAT_PANEL_HEIGHT } from "../../utils/hubLayout";
import {
  GUIDE_FILE_ACCEPT,
  readGuideFile,
  buildProductionSupportTriageMessage,
  type GuideFileReadResult,
} from "../../utils/guideFileReader";

interface ProductionSupportChatPanelProps {
  messages: ProductionSupportChatMessage[];
  onSend: (text: string) => void;
  loading: boolean;
  error: string | null;
  contextUsage?: AgentContextUsage | null;
  quickPrompts?: string[];
  height?: number | string | typeof HUB_CHAT_PANEL_HEIGHT;
}

const ProductionSupportChatPanel: React.FC<ProductionSupportChatPanelProps> = ({
  messages,
  onSend,
  loading,
  error,
  contextUsage = null,
  quickPrompts = [],
  height = HUB_CHAT_PANEL_HEIGHT,
}) => {
  const [input, setInput] = React.useState("");
  const [files, setFiles] = React.useState<GuideFileReadResult[]>([]);
  const [converting, setConverting] = React.useState(false);
  const [convertError, setConvertError] = React.useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const submitGoal = (goal: string, attachments: GuideFileReadResult[] = files) => {
    const trimmed = goal.trim();
    if (!trimmed) return;
    onSend(buildProductionSupportTriageMessage(trimmed, attachments));
  };

  const handleSend = () => {
    if (!input.trim() || converting) return;
    submitGoal(input);
    setInput("");
    setFiles([]);
    setConvertError(null);
  };

  const handleAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (picked.length === 0) return;
    setConverting(true);
    setConvertError(null);
    try {
      const converted: GuideFileReadResult[] = [];
      for (const file of picked) {
        converted.push(await readGuideFile(file));
      }
      setFiles((prev) => [...prev, ...converted]);
    } catch (err: unknown) {
      setConvertError(
        err instanceof Error
          ? err.message
          : "Could not convert attachment to Markdown",
      );
    } finally {
      setConverting(false);
    }
  };

  const busy = loading || converting;

  return (
    <Paper variant="outlined" sx={{ display: "flex", flexDirection: "column", height }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          Ask — goal-driven triage
        </Typography>
        <ContextWindowMeter contextUsage={contextUsage} />
      </Box>
      <Box sx={{ flex: 1, overflow: "auto", p: 2, minHeight: 0 }}>
        {messages.map((m) => (
          <Box
            key={m.id}
            sx={{
              mb: 1.5,
              display: "flex",
              justifyContent: m.role === "user" ? "flex-end" : "flex-start",
            }}
          >
            <AgentChatMessageBubble
              role={m.role}
              content={m.content}
              structuredBlockPreset="production-support"
            />
          </Box>
        ))}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        <div ref={endRef} />
      </Box>
      {(error || convertError) && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
          {convertError || error}
        </Alert>
      )}
      {quickPrompts.length > 0 && (
        <Box sx={{ px: 2, pb: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {quickPrompts.map((p) => (
            <Typography
              key={p}
              variant="caption"
              component="button"
              onClick={() => submitGoal(p, [])}
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                px: 1,
                py: 0.25,
                cursor: "pointer",
                bgcolor: "background.paper",
              }}
            >
              {p}
            </Typography>
          ))}
        </Box>
      )}
      {files.length > 0 && (
        <Box sx={{ px: 2, pb: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {files.map((f, i) => (
            <Chip
              key={`${f.fileName}-${i}`}
              size="small"
              label={f.fileName}
              onDelete={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
            />
          ))}
        </Box>
      )}
      <Box sx={{ p: 2, pt: 0, display: "flex", gap: 1, alignItems: "flex-end" }}>
        <IconButton component="label" disabled={busy} aria-label="Attach incident files">
          <AttachFileIcon />
          <input
            type="file"
            hidden
            multiple
            accept={GUIDE_FILE_ACCEPT}
            onChange={handleAttach}
          />
        </IconButton>
        <TextField
          fullWidth
          size="small"
          required
          placeholder="Goal (required): e.g. Restore partner payments API after 401s"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={busy}
          multiline
          maxRows={4}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={busy || !input.trim()}
        >
          {converting ? <CircularProgress size={20} /> : <SendIcon />}
        </IconButton>
      </Box>
    </Paper>
  );
};

export default ProductionSupportChatPanel;
